import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
import React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { displayCount, type SocialsCount } from "../../lib/socialsCounts";

/**
 * Ch94 · BEHIND THE SCENES — one button, opened only when Grant wants it.
 *
 * PKT-0792: engine last ran · review sync · decisions that failed to apply WITH
 * a view link · token expiry · wording variety — and **amber or red ONLY when
 * Grant must act**. A permanently-red badge is a badge nobody reads.
 *
 * 🔴 THE RED CHIP THIS REPLACES WAS UNCLEARABLE. "2 decisions need Admin
 * review" is a LIFETIME count of exhausted commands
 * (content_review_repository.py:679-690) — no time window, no acknowledge, no
 * way to see WHICH. It could only ever go up. A number that cannot return to
 * zero is not a signal; it is decoration that teaches people to ignore the
 * colour.
 *
 * So every row here answers three questions: what is true, whether Grant must
 * do anything, and — when something failed — WHICH ones, by name.
 */

export interface HealthRow {
  label: string;
  href?: string;
  /** The value, which may honestly be unknown or unavailable. */
  value: SocialsCount | string;
  /**
   * Only "act" earns colour. "fine" and "watch" stay quiet, because a screen
   * that is always amber is a screen with no amber.
   */
  tone: "fine" | "watch" | "act";
  /** What Grant should do. Required when tone is "act" — a red with no action is decoration. */
  action?: string;
  /** Names the specific items behind a failure, so "2 failed" is inspectable. */
  items?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  rows: HealthRow[];
}

const valueText = (value: SocialsCount | string): string =>
  typeof value === "string" ? value : displayCount(value).text;

const BehindTheScenesDialog: React.FC<Props> = ({ open, onClose, rows }) => {
  const needsAction = rows.filter((row) => row.tone === "act");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 7, position: "relative" }}>Behind the scenes<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onClose}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {needsAction.length === 0 ? (
            <Alert severity="info" data-testid="bts-all-clear">
              No action has been identified. Unread checks are listed below.
            </Alert>
          ) : null}

          {rows.map((row) => (
            <Stack key={row.label} spacing={0.5} data-testid="bts-row">
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  color={row.tone === "act" ? "error.main" : "text.primary"}
                >
                  {valueText(row.value)}
                </Typography>
              </Stack>

              {/*
                A failure names its members. "2 decisions failed" that you
                cannot open is the same dead end as the lifetime chip.
              */}
              {row.items && row.items.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {row.items.join(" · ")}
                </Typography>
              ) : null}

              {row.href ? <Button href={row.href} size="small">View decisions</Button> : null}
              {row.tone === "act" && row.action ? (
                <Typography variant="caption" color="error.main">
                  {row.action}
                </Typography>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </DialogContent>

    </Dialog>
  );
};

export default BehindTheScenesDialog;
